import React, { useContext, useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useTranslation } from "react-i18next";

// Components
import PageWrapper from "../../../components/PageWrapper";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import SectionDivider from "../../../components/SectionDivider";
import Loader from "../../../components/Loader";
import AreYouSureModal from "../../../components/AreYouSureModal";
import ProductOrderList from "./ProductOrderList";
import SelectedProductOrderList from "./SelectedProductOrderList";
import InformationBox from "../../../components/InformationBox";

// Utils and Services
import useLanguage from "../../../utils/useLanguage";
import { UserAuth } from "../../../utils/AuthContext";
import { getPaymentCode } from "../../../services/PaymentCodeService";
import {
  createProductOrder,
  getAllProducts,
} from "../../../services/productServices";
import { formatDate } from "../../../utils/toDate";

// Assets
import searchIcon from "../../../assets/search_icon.png";
import saveIcon from "../../../assets/save_icon.png";
import PrintableUniformOrderView from "./PrintableUniformOrder";
import axiosClient from "../../../services/axiosClient";
import ErrorBox from "../../../components/ErrorBox";
import ErrorMessage from "../../../components/ErrorMessage";

const AddUniformOrder = () => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const printRef = useRef(null);

  // Core states
  const [selectedUniforms, setSelectedUniforms] = useState({});
  const [allSelectedUniforms, setAllSelectedUniforms] = useState([]);
  const [allUniformsData, setAllUniformsData] = useState([]);
  const [uniformsData, setUniformsData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Order states
  const [orderDetails, setOrderDetails] = useState({
    orderNumber: "",
    date: new Date(),
    companyName: "",
    createdBy: "",
    notes: "",
  });
  const [receiptNumber, setReceiptNumber] = useState("");
  const [selectedCurrency] = useState({ title: "IQD", value: "IQD" });

  // UI states
  const [loadingUniforms, setLoadingUniforms] = useState(false);
  const [confirmOrderLoading, setConfirmOrderLoading] = useState(false);
  const [showAreYouSure, setAreYouSure] = useState(false);
  const [orderError, setOrderError] = useState(null);

  // Configuration
  const uniformHeaders = [
    { key: "checkbox", label: "choose", type: "checkbox" },
    { key: "size", label: "size", type: "text" },
    { key: "quantity", label: "quantity", type: "input", unit: "piece" },
  ];

  // Print configuration
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 5mm;
        width: 21cm;
        height: 29.7cm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          padding: 10mm;
        }
        .printable-order-view { display: flex !important; }
      }
    `,
  });

  // API Functions
  const getOrderNumber = async () => {
    try {
      const response = await getPaymentCode();
      if (response.status === 200) {
        const orderNumber = response?.data?.code;
        setReceiptNumber(orderNumber);
        const isUser = userData?.userType === "orgUser";
        setOrderDetails((prev) => ({
          ...prev,
          orderNumber,
          companyName: isUser ? userData.organizationName : "",
          createdBy: isUser ? userData.username : "",
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getAllUniforms = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingUniforms(true);
      const response = await axiosClient.get("/productRuler", {
        params: {
          organizationId,
          limit: 10000,
        },
      });

      if (response.status === 200 && response.data) {
        const items = response.data.productRulers?.map((item) => ({
          id: item.id,
          name: item.name,
          color: item.color,
          sizes: item.productRulerItem?.map((item) => ({
            id: item.id,
            size: item.size,
            price: item.price,
            notes: item.notes,
          })),
        }));
        setUniformsData(items);
        setAllUniformsData(items);
      }
    } catch (err) {
      console.error("Failed to fetch uniforms:", err);
    } finally {
      setLoadingUniforms(false);
    }
  };

  // Utility Functions
  const filterUniforms = (query) => {
    if (!query.trim()) {
      setUniformsData(allUniformsData);
      return;
    }

    const filtered = allUniformsData
      .map((category) => {
        const filteredSizes =
          category.sizes?.filter(
            (uniform) =>
              uniform.name?.toLowerCase().includes(query.toLowerCase()) ||
              uniform.size?.toLowerCase().includes(query.toLowerCase())
          ) || [];

        return filteredSizes.length > 0
          ? { ...category, sizes: filteredSizes }
          : null;
      })
      .filter(Boolean);

    setUniformsData(filtered);
  };

  const validateSelectedUniforms = () => {
    const errors = [];
    allSelectedUniforms.forEach((category) => {
      category.sizes.forEach((size) => {
        const quantity = parseInt(size.quantity) || 0;
        if (quantity <= 0) {
          errors.push(`${category.name} - ${size.size}`);
        }
      });
    });
    return errors;
  };

  const canConfirmOrder = () => {
    if (allSelectedUniforms.length === 0) return false;
    return allSelectedUniforms.every((category) =>
      category.sizes.every((size) => {
        const quantity = parseInt(size.quantity) || 0;
        return quantity > 0;
      })
    );
  };

  // Event Handlers
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    filterUniforms(query);
  };

  const handleSearchClick = () => {
    filterUniforms(searchQuery);
  };

  const handleConfirmOrder = () => {
    const validationErrors = validateSelectedUniforms();
    if (validationErrors.length > 0) {
      const errorMessage =
        t("pleaseProvideQuantityFor") + ":\n" + validationErrors.join("\n");
      alert(errorMessage);
      return;
    }
    setAreYouSure(true);
  };

  const handleCreateOrder = async () => {
    setConfirmOrderLoading(true);
    setOrderError(null);

    try {
      const isUser = userData?.userType === "orgUser";
      const orderData = {
        orderNumber: orderDetails.orderNumber,
        date: orderDetails.date || new Date().toISOString(),
        type: "uniform",
        notes: orderDetails.notes,
        orders: allSelectedUniforms
          .map((category) =>
            category.sizes.map((size) => ({
              productRulerItemId: size.id,
              requestedQuantity: parseInt(size.quantity) || 0,
              ruler: true,
            }))
          )
          .flat(),
          userId: isUser ? parseInt(userData.userId) : null,
      };

      const response = await createProductOrder(orderData);
      if (response.status === 201) {
        handlePrint();
        resetToOriginalData();
        setAreYouSure(false);
        return response.data;
      } else {
        throw new Error(response.data?.error || "Failed to create order");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      setOrderError(t("createUniformOrderError"));
    } finally {
      setConfirmOrderLoading(false);
    }
  };

  const handlePrintAndSave = async () => {
    try {
      await handleCreateOrder();
    } catch (error) {
      console.error("Error saving order:", error);
    }
  };

  const handleUniformSelection = (categoryId, sizeId, isSelected) => {
    setOrderError(null);
    setSelectedUniforms((prev) => {
      if (sizeId === null) {
        const { [categoryId]: removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          [sizeId]: isSelected,
        },
      };
    });

    if (sizeId === null) {
      setAllSelectedUniforms((prev) =>
        prev.filter((item) => item.name !== categoryId)
      );
    } else {
      if (isSelected) {
        const category = allUniformsData.find((cat) => cat.name === categoryId);
        if (category) {
          const size = category.sizes.find((s) => s.id === sizeId);
          if (size) {
            setAllSelectedUniforms((prev) => {
              const categoryIndex = prev.findIndex(
                (cat) => cat.name === categoryId
              );

              if (categoryIndex >= 0) {
                const sizeExists = prev[categoryIndex].sizes.some(
                  (s) => s.id === sizeId
                );
                if (!sizeExists) {
                  const updatedCategories = [...prev];
                  updatedCategories[categoryIndex] = {
                    ...updatedCategories[categoryIndex],
                    sizes: [
                      ...updatedCategories[categoryIndex].sizes,
                      { ...size },
                    ],
                  };
                  return updatedCategories;
                }
                return prev;
              } else {
                return [
                  ...prev,
                  {
                    id: category.id,
                    name: categoryId,
                    color: category.color,
                    sizes: [{ ...size }],
                  },
                ];
              }
            });
          }
        }
      } else {
        setAllSelectedUniforms((prev) => {
          const updatedCategories = prev.map((cat) => {
            if (cat.name === categoryId) {
              return {
                ...cat,
                sizes: cat.sizes.filter((size) => size.id !== sizeId),
              };
            }
            return cat;
          });
          return updatedCategories.filter((cat) => cat.sizes.length > 0);
        });
      }
    }
  };

  const handleInputChange = (categoryId, sizeId, field, value) => {
    setOrderError(null);
    setAllSelectedUniforms((prev) => {
      return prev.map((category) => {
        if (category.name === categoryId) {
          return {
            ...category,
            sizes: category.sizes.map((size) => {
              if (size.id === sizeId) {
                const updatedSize = { ...size, [field]: value || 0 };
                if (field === "quantity") {
                  updatedSize.totalPrice =
                    (updatedSize.price || 0) * (value || 0);
                }
                return updatedSize;
              }
              return size;
            }),
          };
        }
        return category;
      });
    });
  };

  const resetToOriginalData = () => {
    setUniformsData(allUniformsData);
    setSearchQuery("");
    setAllSelectedUniforms([]);
    getOrderNumber();
    setSelectedUniforms([]);
  };

  // Effects
  useEffect(() => {
    getAllUniforms();
  }, []);

  useEffect(() => {
    getOrderNumber();
  }, []);

  return (
    <PageWrapper title={t("addUniformOrder")} withBackButton>
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500 u-margin--top_700">
        {/* Search and Order Info */}
        <div className="o-column-row-switcher-reversed o-column--horizontal-align_space-between o-row--gap-size_300">
          <div className="c-search-bar">
            <section className="c-search-bar-label-input">
              <CustomInput
                placeholder={t("search")}
                value={searchQuery}
                onChange={handleSearchChange}
                resetSearch={() => {
                  setSearchQuery("");
                  setUniformsData(allUniformsData);
                }}
                thick
              />
            </section>

            <CustomButton
              title={t("search")}
              iconSize={15}
              classList="c-search-button"
              icon={searchIcon}
              onClick={handleSearchClick}
            />
          </div>
          <div className="o-row o-row--gap-size_300">
            <CustomInput
              title={t("orderNumber") + ": "}
              value={receiptNumber}
              flex={false}
              readonly
              loading={loadingUniforms}
            />
            <CustomInput
              type="date"
              title={t("Date") + ": "}
              value={formatDate(orderDetails.date)}
              onChange={(e) => {
                setOrderDetails((prev) => ({
                  ...prev,
                  date: new Date(e.target.value),
                }));
              }}
              flex={false}
              loading={loadingUniforms}
            />
          </div>
        </div>

        <SectionDivider title={t("uniforms")} />

        {loadingUniforms && <Loader />}

        <ProductOrderList
          data={uniformsData}
          selectedItems={selectedUniforms}
          onItemSelect={handleUniformSelection}
          currency={selectedCurrency}
          onInputChange={handleInputChange}
          allSelectedItems={allSelectedUniforms}
          headers={uniformHeaders}
          groupByField="name"
          itemsField="sizes"
          itemKeyField="id"
          groupLabelPrefix="uniformName"
        />

        {orderError && <ErrorBox>{orderError}</ErrorBox>}
        {allSelectedUniforms.length > 0 && (
          <SectionDivider title={t("theAddedUniforms")} />
        )}

        <SelectedProductOrderList
          data={allSelectedUniforms}
          setSelectedItems={setAllSelectedUniforms}
          currency={selectedCurrency}
          selectedItems={selectedUniforms}
          onItemSelect={handleUniformSelection}
          onInputChange={handleInputChange}
          headers={uniformHeaders}
          groupByField="name"
          itemsField="sizes"
          itemKeyField="id"
          groupLabelPrefix="uniformName"
          showDeleteButton={true}
          deleteButtonLabel="delete"
        />

        {allSelectedUniforms.length > 0 && (
          <CustomInput
            title={`${t("notes")}: `}
            type="textarea"
            onChange={(e) => {
              setOrderDetails((prev) => ({
                ...prev,
                notes: e.target.value,
              }));
            }}
          />
        )}

        {orderError && <ErrorMessage>{orderError}</ErrorMessage>}

        {/* Validation message */}
        {allSelectedUniforms.length > 0 && !canConfirmOrder() && (
          <InformationBox>
            {t("pleaseProvideQuantityForAllSelectedBooks")}
          </InformationBox>
        )}

        <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
          {allSelectedUniforms.length > 0 && (
            <CustomButton
              title={t("confirm")}
              onClick={handleConfirmOrder}
              icon={saveIcon}
              iconSize={16}
              disabled={confirmOrderLoading || !canConfirmOrder()}
              style={{
                opacity: !canConfirmOrder() ? 0.6 : 1,
                cursor: !canConfirmOrder() ? "not-allowed" : "pointer",
              }}
            />
          )}
        </div>
      </div>

      {showAreYouSure && (
        <AreYouSureModal
          onClose={() => setAreYouSure(false)}
          onConfirm={handlePrintAndSave}
          customMessage={t("confirmOrderMessage")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirm
          confirmButtonVariant={null}
          error={orderError}
          loading={confirmOrderLoading}
          confirmIconType="whiteCheck"

        />
      )}

      <PrintableUniformOrderView
        ref={printRef}
        data={allSelectedUniforms.map((uniform) => ({
          ...uniform,
          sizes: uniform.sizes.map((size) => ({
            id: size.id,
            notes: size.notes,
            quantity: size.quantity,
            size: size.size,
          })),
        }))}
        currency={selectedCurrency}
        orderDetails={orderDetails}
      />
    </PageWrapper>
  );
};

export default AddUniformOrder;
